import { map, tap } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { differenceInDays } from 'date-fns';

import { LocalStorage } from '@app/services/local-storage';
import { DocumentsService, IDocument } from '@app/admin/fleet-card/card-application/documents.service';
import { FleetCardsDataService } from '@app/services/fleet-cards-data';
import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';

import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';
import { IPotentialSavingsObject } from '@app/services/fleet-cards-data';

export const FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE = {
  potentialSavingsApplyNow: 'potentialSavingsApplyNow',
  potentialSavingsFinishApplication: 'potentialSavingsFinishApplication',
  linkBankAccount: 'linkBankAccount',
  fleetBundle3DMessage: 'fleetBundle3DMessage',
  fleetBundle15DMessage: 'fleetBundle15DMessage',
  fleetBundle45DMessage: 'fleetBundle45DMessage',
};

export const NUDGE_POPOVER_DELAY: number = 60000;
const MINIMUM_CONTRACT_VALUE_THRESHOLD: number = 250;
const MINIMUM_REWARD_THRESHOLD: number = 25;

export const FLEET_BUNDLE_MESSAGE_TYPE = ['fleetBundle3DMessage', 'fleetBundle15DMessage', 'fleetBundle45DMessage'];

export const PLAID_URL = 'https://plaid.com/how-we-handle-data/';

export const REWARD_TYPE = {
  tcv: 'TCV',
  acv: 'ACV',
};

export const UPSELL_TYPE = {
  popover: 'popover',
  banner: 'banner',
};

export interface ITcvRewardsObject {
  rewardPercentage: number;
  rewardValue: number;
  daysRemainingUntilExpiry: number;
}

export const FLEET_CARDS_APPLICATION_NUDGE_TEXT_CONFIG = {
  potentialSavingsApplyNow: {
    header: (potentialSavings: string): string => {
      return $localize`Save up to ${potentialSavings} annually with the Motive Card`;
    },
    body: $localize`Completing your Motive Card application will take less than 5 minutes and you’ll get a decision within 3 days.`,
    btnText: $localize`Apply now`,
  },
  potentialSavingsFinishApplication: {
    header: $localize`Unlock big savings for your fleet`,
    body: (potentialSavings: string) => {
      return $localize`Finish your Motive Card application with your bank and business details and unlock up to ${potentialSavings}/year`;
    },
    btnText: $localize`Finish application`,
  },
  linkBankAccount: {
    header: $localize`Unlock up to $10,000 Savings with the Motive Card!`,
    body: $localize`Didn't finish linking your bank account? Upload 3 months of statements to complete your application and start saving.`,
    btnText: $localize`Upload statements`,
  },
  fleetBundle45DMessage: {
    header: (rewardPercentage: number): string => {
      return $localize`Limited time offer: Save ${rewardPercentage}% or more of your total contract value when you sign up for the Motive Card`;
    },
    body: (rewardValue: string): string => {
      return $localize`Apply for Motive Card now and earn ${rewardValue} as Motive Card Rewards upon approval.`;
    },
    subBody: $localize`Motive Card Rewards are redeemed at 1% of future Motive Card spend.`,
    btnText: $localize`Apply now`,
  },
  fleetBundle15DMessage: {
    header: (rewardPercentage: number, daysRemainingUntilExpiry: number): string => {
      return $localize`Your ${rewardPercentage}% savings offer expires in ${daysRemainingUntilExpiry} days`;
    },
    body: (rewardPercentage: number): string => {
      return $localize` Apply for Motive Card now and save ${rewardPercentage}% or more of your total contract value.`;
    },
    subBody: (rewardValue: string): string => {
      return $localize` Upon approval you’ll earn ${rewardValue} as Motive Card Rewards based on your current contract value. Rewards are redeemed at 1% of future Motive Card spend.`;
    },
    btnText: $localize`Apply now`,
  },
  fleetBundle3DMessage: {
    header: (rewardValue: string): string => {
      return $localize`Last chance to claim ${rewardValue} in Motive Card Rewards`;
    },
    body: (rewardValue: string): string => {
      return $localize` Apply for Motive Card now and earn ${rewardValue} as Motive Card Rewards upon approval`;
    },
    btnText: $localize`Apply now`,
  },
};

export const setNextDisplayDate = (localStorage: LocalStorage, messageType: string): void => {
  const dismissCount: number = getDismissCount(localStorage, messageType);
  setDismissCount(localStorage, messageType);

  if ((dismissCount + 1) >= 3) return;

  const nextDisplayDate: Date = new Date();
  nextDisplayDate.setDate(nextDisplayDate.getDate() + 7);

  localStorage.setFleetCardApplicationNudgeNextDisplayDate(nextDisplayDate.toISOString());
};

export const evaluateBannerOrPopoverVisibility = (
  localStorage: LocalStorage,
  messageType: string,
  potentialSavings: number,
  company: IFleetCardCompany,
  upsellType: string,
  fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
): boolean => {
  const currentDate: Date = new Date();

  const fleetCardApplicationNudgeNextDisplayDate: string = localStorage.getFleetCardApplicationNudgeNextDisplayDate();
  const fleetCardApplicationfirstVisitedDate: string = localStorage.getFleetCardApplicationFirstVisitedDate();
  const previousMessageType: string = localStorage.getFleetCardApplicationNudgeExperimentMessageType();
  const nextDisplayDate: Date =
    fleetCardApplicationNudgeNextDisplayDate ? new Date(fleetCardApplicationNudgeNextDisplayDate) : null;
  const firstCardApplicationVisitedDate: Date =
    fleetCardApplicationfirstVisitedDate ? new Date(fleetCardApplicationfirstVisitedDate) : null;
  const experimentUpsellType: string = fleetCardsFeatureFlagService.getFleetCardsApplicationNudgeExperimentValue();

  // Show the upsell 1 day after the user clicks on Apply Now from anywhere in the application.
  if (firstCardApplicationVisitedDate) firstCardApplicationVisitedDate.setDate(firstCardApplicationVisitedDate.getDate() + 1);

  // Retrieve the dismiss counts for the current and previous message types.
  const dismissCount: number = getDismissCount(localStorage, messageType);
  const fleetBundleDismissCount: number = getFleetBundleDismissCount(localStorage, messageType);
  const previousMessageTypeDismissCount: number = getDismissCount(localStorage, previousMessageType);

  // Ensure that the company status is either 'application_started' or 'upsell'. If not, return false.
  if (!['application_started', 'upsell'].includes(company.status)) return false;

  // Check if the messageType belongs to the Fleet Bundle message types.
  if (FLEET_BUNDLE_MESSAGE_TYPE.includes(messageType)) {
    // If the upsell type is a banner or the dismiss count for Fleet Bundle is 1 or more, return false.
    if (upsellType === UPSELL_TYPE.banner || fleetBundleDismissCount >= 1) return false;
    return true;
  } else {
    // For non-Fleet Bundle message types, ensure the upsell type matches the experiment upsell type.
    if (upsellType !== experimentUpsellType) return false;
  }

  // If the dismiss count for the current message type is 3 or more, return false.
  if (dismissCount >= 3) return false;

  // If the message type is not 'linkBankAccount' and the potential savings is zero, return false.
  if (messageType !== FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount && potentialSavings === 0) return false;

  // Update the localStorage with the current message type.
  localStorage.setFleetCardApplicationNudgeExperimentMessageType(messageType);

  // If there is a next display date stored.
  if (fleetCardApplicationfirstVisitedDate && nextDisplayDate) {
    // Check if the previous message type is different, current date is after the next display date,
    // and previous message type dismiss count is 3 or more.
    const shouldUpdateNextDisplayDate =
      previousMessageType !== messageType && currentDate > nextDisplayDate && previousMessageTypeDismissCount >= 3;

    if (shouldUpdateNextDisplayDate) {
      // Update the next display date by adding 7 days and store it in localStorage.
      const updatedNextDisplayDate: Date = new Date();
      updatedNextDisplayDate.setDate(updatedNextDisplayDate.getDate() + 7);
      localStorage.setFleetCardApplicationNudgeNextDisplayDate(updatedNextDisplayDate.toISOString());
      return false; // Do not display the banner or popover.
    }
    // If the current date is past the next display date, allow the banner or popover to be shown.
    return currentDate >= nextDisplayDate;
  }

  // If there is no next display date but there is a first visited date,
  // check if the current date is past the first visited date (plus one day).
  return fleetCardApplicationfirstVisitedDate ? currentDate >= firstCardApplicationVisitedDate : false;
};

export const getDismissCount = (localStorage: LocalStorage, messageType: string): number => {
  if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsApplyNow) {
    return localStorage.getFleetCardApplicationNudgeApplyNowDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsFinishApplication) {
    return localStorage.getFleetCardApplicationNudgeFinishAppDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount) {
    return localStorage.getFleetCardApplicationNudgeLinkBankAccDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle3DMessage) {
    return localStorage.getFleetCardApplicationFleetBundle3DDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle15DMessage) {
    return localStorage.getFleetCardApplicationFleetBundle15DDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle45DMessage) {
    return localStorage.getFleetCardApplicationFleetBundle45DDismissCount();
  }
};

export const getFleetBundleDismissCount = (localStorage: LocalStorage, messageType: string): number => {
  if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle3DMessage) {
    return localStorage.getFleetCardApplicationFleetBundle3DDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle15DMessage) {
    return localStorage.getFleetCardApplicationFleetBundle15DDismissCount();
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle45DMessage) {
    return localStorage.getFleetCardApplicationFleetBundle45DDismissCount();
  }
};


export const setDismissCount = (localStorage: LocalStorage, messageType: string): void => {
  if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsApplyNow) {
    localStorage.setFleetCardApplicationNudgeApplyNowDismissCount
      (localStorage.getFleetCardApplicationNudgeApplyNowDismissCount() + 1);
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsFinishApplication) {
    localStorage.setFleetCardApplicationNudgeFinishAppDismissCount
      (localStorage.getFleetCardApplicationNudgeFinishAppDismissCount() + 1);
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount) {
    localStorage.setFleetCardApplicationNudgeLinkBankAccDismissCount
      (localStorage.getFleetCardApplicationNudgeLinkBankAccDismissCount() + 1);
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle3DMessage) {
    localStorage.setFleetCardApplicationFleetBundle3DDismissCount
      (localStorage.getFleetCardApplicationFleetBundle3DDismissCount() + 1);
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle15DMessage) {
    localStorage.setFleetCardApplicationFleetBundle15DDismissCount
      (localStorage.getFleetCardApplicationFleetBundle15DDismissCount() + 1);
  } else if (messageType === FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle45DMessage) {
    localStorage.setFleetCardApplicationFleetBundle45DDismissCount
      (localStorage.getFleetCardApplicationFleetBundle45DDismissCount() + 1);
  }
};

export const isBankAccountLinked = (company: IFleetCardCompany): boolean => {
  if (company.paymentMethod?.isDefault || company.paymentMethod?.virtualAccount) return true;
  return false;
};

export const isDocumnetsUploaded = (documentsService: DocumentsService): Observable<IDocument[]> => {
  return documentsService.getAll().pipe(map(response => response));
};

export const setUpChecks = (
  upsellType: string,
  fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
  localStorage: LocalStorage,
  documentsService: DocumentsService,
  fleetCardsDataService: FleetCardsDataService,
  setDocumentsUploaded: (uploaded: boolean) => void,
  setCompany: (company: IFleetCardCompany) => void,
  setBankAccountLinked: (linked: boolean) => void,
  setMessageType: (messageType: string) => void,
  setPotentialSavings: (savings: number) => void,
  showBannerOrPopover: () => void,
  setRewardDetails?: (rewardDetails: ITcvRewardsObject) => void,
): void => {
  combineLatest([
    fleetCardsDataService.getCompanyCardMetadataStream(false),
    isDocumnetsUploaded(documentsService),
    fleetCardsDataService.getCompanyPotentialSavings(),
  ]).subscribe(([company, documents, savings]) => {
    const documentsUploaded: boolean = documents.length > 0;
    const bankAccountLinked: boolean = isBankAccountLinked(company);
    const messageType: string =
      getMessageType(
        localStorage.getFleetCardLinkBankAccountClicked(),
        documentsUploaded,
        bankAccountLinked,
        company.status,
        savings,
        fleetCardsFeatureFlagService,
        localStorage,
      );
    const potentialSavings: number = getPotentialSavings(savings, fleetCardsFeatureFlagService);
    const rewardDetails: ITcvRewardsObject = getRewardDetails(savings);

    if (evaluateBannerOrPopoverVisibility(
      localStorage,
      messageType,
      potentialSavings,
      company,
      upsellType,
      fleetCardsFeatureFlagService,
    )) {
      setDocumentsUploaded(documentsUploaded);
      setCompany(company);
      setBankAccountLinked(bankAccountLinked);
      setMessageType(messageType);
      setPotentialSavings(potentialSavings);
      showBannerOrPopover();
      if (setRewardDetails) setRewardDetails(rewardDetails);
    }

  });
};

export const getPotentialSavings = (savings: IPotentialSavingsObject,
  fleetCardsFeatureFlagService: FleetCardsFeatureFlagService): number => {
  let potentialSavings: number = 0;

  potentialSavings = savings.annualizedFuelRebates
    + savings.annualizedFraudSavings
    + savings.annualizedOperationalSavings;

  if (fleetCardsFeatureFlagService.isFleetCardTCVACVRewardsEnabled) {
    if (savings.contractInfo.rewardType === REWARD_TYPE.tcv) {
      potentialSavings += savings.contractInfo.totalContractValue * savings.contractInfo.rewardPercentageDecimal;
    } else if (savings.contractInfo.rewardType === REWARD_TYPE.acv) {
      potentialSavings += savings.contractInfo.annualContractValue * savings.contractInfo.rewardPercentageDecimal;
    }
  }

  return Math.round(potentialSavings);
};

export const getRewardDetails = ({ contractInfo }: IPotentialSavingsObject): ITcvRewardsObject => {
  const { customerAcquisitionDate, rewardPercentageDecimal, totalContractValue } = contractInfo;
  const acquisitionDate = new Date(customerAcquisitionDate);
  const daysSinceAcquisition = differenceInDays(new Date(), acquisitionDate);
  const daysRemainingUntilExpiry = 90 - daysSinceAcquisition;

  return {
    rewardPercentage: rewardPercentageDecimal * 100,
    rewardValue: totalContractValue * rewardPercentageDecimal,
    daysRemainingUntilExpiry,
  };
};

export const getMessageType = (fleetCardLinkBankAccountClicked: boolean, documentsUploaded: boolean,
  bankAccountLinked: boolean, companyStatus: string, savings: IPotentialSavingsObject,
  fleetCardsFeatureFlagService: FleetCardsFeatureFlagService, localStorage: LocalStorage): string => {
  const fleetCardApplicationfirstVisitedDate: string = localStorage.getFleetCardApplicationFirstVisitedDate();
  const isEligibleForTCVRewards: boolean = !fleetCardApplicationfirstVisitedDate &&
    fleetCardsFeatureFlagService.isFleetCardTCVACVRewardsEnabled() &&
    fleetCardsFeatureFlagService.isFleetCardsFleetBundleExperimentEnabled() &&
    savings.contractInfo.rewardType === REWARD_TYPE.tcv &&
    savings.contractInfo.totalContractValue > MINIMUM_CONTRACT_VALUE_THRESHOLD &&
    savings.contractInfo.totalContractValue * savings.contractInfo.rewardPercentageDecimal > MINIMUM_REWARD_THRESHOLD;
  const customerAcquisitionDate: string = savings.contractInfo.customerAcquisitionDate;
  const currentDate: Date = new Date();
  const acquisitionDate: Date = new Date(customerAcquisitionDate);
  const daysSinceAcquisition: number = differenceInDays(currentDate, acquisitionDate);
  const daysRemainingUntilExpiry: number = 90 - daysSinceAcquisition;

  if (fleetCardLinkBankAccountClicked && !documentsUploaded && !bankAccountLinked) {
    return FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.linkBankAccount;
  } else if (companyStatus === 'application_started') {
    return FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsFinishApplication;
  } else if (isEligibleForTCVRewards) {
    if (daysRemainingUntilExpiry <= 3) {
      return FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle3DMessage;
    } else if (daysRemainingUntilExpiry <= 15) {
      return FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle15DMessage;
    } else if (daysRemainingUntilExpiry <= 45) {
      return FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.fleetBundle45DMessage;
    }
  } else {
    return FLEET_CARDS_APPLICATION_NUDGE_MESSAGE_TYPE.potentialSavingsApplyNow;
  }
};
